import VideoCreatorStore from '@src/stores/VideoCreatorStore';
import {
  ShareableImagePreviewType,
  ShareableImageType,
  VideoClip,
} from '../types.ts/story';
import { Platform, CaptionData } from '@src/types.ts/caption';
import ChatGPTService from './ChatGPTService';
import { logChatGptOutput } from '@src/utility/datadog';
import {
  StoryRepository,
  CaptionRepository,
  AIPromptRepository,
} from '@src/repositories';

type Label = 'otherVideos' | 'shareableImages';

export default class CaptionService extends ChatGPTService {
  private storyRepository: StoryRepository;
  private captionRepository: CaptionRepository;

  constructor(
    videoCreator: VideoCreatorStore,
    storyRepository: StoryRepository,
    captionRepository: CaptionRepository,
    aiPromptRepository: AIPromptRepository,
  ) {
    // TODO: DatoClientStore integration
    super(videoCreator, aiPromptRepository);

    this.storyRepository = storyRepository;
    this.captionRepository = captionRepository;
  }

  public getExistingCaptionData<T extends Label>(
    data: Pick<CaptionData<T>, 'platform' | 'contentId' | 'label'>,
  ) {
    const content = (
      this.videoCreator.story?.[data.label] as (
        | VideoClip
        | ShareableImageType
      )[]
    )?.find((c) => {
      return c.id === data.contentId;
    });

    const platformData = content?._allReferencingCaptions?.find(
      (r) => r.platform === data.platform,
    );
    return { platformData, content };
  }

  public getExistingCaptionText<T extends Label>(
    data: Pick<CaptionData<T>, 'platform' | 'contentId' | 'label'>,
  ) {
    const { platformData } = this.getExistingCaptionData(data);
    return platformData?.caption || '';
  }

  private getPromptLabel(label: Label) {
    if (label === 'otherVideos') {
      return 'Social Posts';
    }
    return 'Sharable Image Caption';
  }

  private getFullTranscription = () => {
    return this.transcriptionStore
      .getFinalTranscriptionElements()
      ?.map((e) => e.value || '')
      .join('');
  }

  private getRequestInputData = async <T extends Label>(
    customInstructions: string | null,
    data: CaptionData<T>,
  ) => {
    const transcript = data.useFullTranscription
      ? this.getFullTranscription()
      : data.transcriptionText || this.getFullTranscription();

    let promptKey = this.getPromptLabel(data.label);
    const aiPrompt = await this.fetchAiPrompt(promptKey);
    if (!aiPrompt || !transcript || !this.videoCreator.storyId) return;

    const field = aiPrompt.promptFields.find(
      (f) => f.name.toLowerCase() === data.platform,
    );

    let systemMessage = aiPrompt.description + (field?.description || '');

    if (customInstructions) {
      systemMessage += `${systemMessage ? '\n\n' : ''}${customInstructions}`;
    }

    const brandVoice = this.videoCreator.organization?.branding?.brandVoice;
    if (brandVoice) {
      const brandVoicePrompt = await this.fetchAiPrompt('Brand Voice');
      if (brandVoicePrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          brandVoicePrompt.description
        }\nBrand voice: ${brandVoice}`;
      }
    }

    const avoidWords = this.videoCreator.organization?.branding?.avoidWords;
    if (avoidWords) {
      const avoidWordsPrompt = await this.fetchAiPrompt('Avoid Words');
      if (avoidWordsPrompt) {
        systemMessage += `${systemMessage ? '\n\n' : ''}${
          avoidWordsPrompt.description
        }\nAvoid words: ${avoidWords}`;
      }
    }

    return {
      systemMessage,
      transcript,
    };
  };

  public async saveCaptionsToDato<T extends Label>(data: CaptionData<T>) {
    const story = this.videoCreator?.story;
    if (!story?.id) return;

    const { text } = data;

    if (!data.id) {
      const savedId = await this.captionRepository.createCaption({
        title: `${story.title} - ${data.platform}`,
        platform: data.platform,
        text: text,
        storyId: story.id,
        shareableId: data.contentId,
      });
      this.updateCaptionInStore({ ...data, id: savedId });
      return savedId;
    } else {
      await this.captionRepository.updateCaption(data.id, {
        text: text,
        shareableId: data.contentId,
      });
      this.updateCaptionInStore(data);
    }
  }

  public async regenerateCaptionStreamResponse<T extends Label>(
    customInstructions: string | null = null,
    toggleLoading: (e: boolean) => void,
    data: CaptionData<T>,
    callback: (savedId: string) => void,
    inputData: Record<'systemMessage' | 'transcript', string> | null = null,
  ) {
    const controller = new AbortController();
    const signal = controller.signal;

    const items =
      inputData || (await this.getRequestInputData(customInstructions, data));
    if (!items) return;
    const { systemMessage, transcript } = items;

    const { platformData, content } = this.getExistingCaptionData(data);

    const existingResponse = platformData?.caption || '';

    const storyId = this.videoCreator.story?.id!;

    try {
      const resPromise = fetch(
        `${process.env.REACT_APP_API_URL}/api/aiprompts/regenerate-gpt-response-stream`,
        {
          signal,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            systemMessage,
            transcript,
            existingResponse,
            storyId,
          }),
        },
      );

      this.updateCaptionInStore({ ...data, text: '' });
      this.streamProcessDone = false;
      const res = await resPromise;
      if (!res.body) return;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();

      while (!this.streamProcessDone) {
        const { value, done: doneReading } = await reader.read();
        this.streamProcessDone = doneReading;
        let chunk = decoder.decode(value);
        toggleLoading(false);

        const existingResponse = this.getExistingCaptionText(data);
        let text = existingResponse;
        if (
          existingResponse.endsWith('.') &&
          chunk &&
          (!chunk.startsWith(' ') || !chunk.startsWith('\n'))
        ) {
          text += ` ${chunk}`;
        } else {
          text += chunk;
        }
        if (doneReading) {
          logChatGptOutput({ 'regenerate-gpt-response-stream': text });
        }

        this.updateCaptionInStore({ ...data, text });
      }

      if (this.streamProcessDone) {
        if (!content && data.label === 'shareableImages') {
          const savedId = await this.saveShareableImage(data.shareable!);
          data.contentId = savedId;
          callback(savedId!);
        }
        data.text = this.getExistingCaptionText(data);
        await this.saveCaptionsToDato(data);
      }
    } catch (error) {
      console.log('Error: ', error);
    } finally {
      toggleLoading(false);
    }
  }

  public updateCaptionInStore = <T extends Label>(data: CaptionData<T>) => {
    const { id, contentId, label, platform, text } = data;
    const storyId = this.videoCreator.storyId!;
    let idx = this.videoCreator?.story?.[label].findIndex(
      (content) => content.id === contentId,
    );

    if (!contentId) return;

    if ((idx === undefined || idx <= -1) && label === 'shareableImages') {
      this.videoCreator.story!.shareableImages = [
        ...(this.videoCreator.story!.shareableImages || []),
        {
          id: contentId!,
          imagefile: data.shareable?.imageFile,
          quote: data.shareable?.quote,
          storytellerName: this.videoCreator.story?.storyTeller?.name || '',
        },
      ] as ShareableImageType[];
      idx = this.videoCreator.story!.shareableImages.length - 1;
    }

    const isNew = !this.videoCreator!.story![label][
      idx!
    ]._allReferencingCaptions?.find((c) => c.platform === platform);

    if (isNew) {
      this.videoCreator!.story![label][idx!]._allReferencingCaptions = [
        ...(this.videoCreator!.story![label][idx!]._allReferencingCaptions ||
          []),
        {
          id,
          platform,
          story: {
            id: storyId!,
          },
          shareable: {
            __typename:
              label === 'otherVideos' ? 'VideoRecord' : 'ShareableImageRecord',
          },
          caption: text.replace(/^["'\s-]+|["'\s-]+$/g, '')?.trim(),
        },
      ];
    } else {
      this.videoCreator!.story![label][idx!]._allReferencingCaptions =
        this.videoCreator!.story![label][idx!]._allReferencingCaptions?.map(
          (caption) => {
            if (caption.platform === platform) {
              return {
                ...caption,
                id,
                shareable: {
                  __typename:
                    label === 'otherVideos'
                      ? 'VideoRecord'
                      : 'ShareableImageRecord',
                },
                caption: text.replace(/^["'\s-]+|["'\s-]+$/g, '')?.trim(),
              };
            }
            return caption;
          },
        );
    }
  };

  public async saveShareableImage(shareable: ShareableImagePreviewType) {
    const unsavedIdx = this.videoCreator.story!.shareableImages.length - 1;
    const unsaved = this.videoCreator.story!.shareableImages[unsavedIdx];

    const savedId = await this.storyRepository?.createShareableImage(
      this.videoCreator.story!,
      shareable.quote,
      shareable.imageFile,
    );

    this.videoCreator.unsavedShareableImages =
      this.videoCreator.unsavedShareableImages?.filter(
        (o) => o.id !== unsaved.id,
      ) || [];

    this.videoCreator!.story!.shareableImages[unsavedIdx].id = savedId!;
    await this.videoCreator.updateStory(this.videoCreator.story!);

    return savedId;
  }

  public async generateAllCaptions(contentId: string, key = 'Social Posts', transcriptText?: string) {
    const transcript = transcriptText || this.getFullTranscription();
    const aiPrompt = await this.fetchAiPrompt(key);
    if (!aiPrompt || !transcript || !this.videoCreator.storyId) return;

    let systemMessage = aiPrompt.description;

    await Promise.all(
      aiPrompt.promptFields.map(async (f) => {
        const platform = f.name.toLowerCase() as Lowercase<Platform>;
        const label =
          key === 'Social Posts' ? 'otherVideos' : ('shareableImages' as Label);

        const captionData = this.getExistingCaptionData({
          platform,
          label,
          contentId,
        });
        if (!captionData.platformData?.caption) {
          const data = {
            platform: f.name.toLowerCase() as Lowercase<Platform>,
            text: '',
            label:
              key === 'Social Posts'
                ? 'otherVideos'
                : ('shareableImages' as Label),
            contentId,
            id: captionData.platformData?.id,
            useFullTranscription: false,
          };

          const captionService = new CaptionService(
            this.videoCreator,
            this.storyRepository,
            this.captionRepository,
            this.aiPromptRepository,
          );
          await captionService.regenerateCaptionStreamResponse(
            '',
            () => {},
            data,
            () => {},
            { systemMessage, transcript },
          );
        }
      }),
    );
  }
}
